import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BedDouble, Bath, Wifi, Tv, Coffee, Wind, ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from './Reveal';

const apartments = [
  {
    id: 1,
    title: "Standart 1+1 Daire",
    description: "Kısa süreli konaklamalar için ideal, konforlu ve şık tasarımlı yaşam alanı.",
    images: [
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/t5z42cgg_1%2B1%20kapak.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/36on7b6g_D-107.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/ivyjvbkj_D-108.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/pus0be4z_D-109.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/l3is9rjt_D-110.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/k2yx573e_D-111.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/mabnsh0r_D-112.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/o7bjfmlw_D-113.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/4700utxs_D-114.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/wj8ocrn4_D-115.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/t8eipmqh_D-20%20Melih.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/ey5o9s1g_D-118.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/5ll8oc1g_D-122.jpeg"
    ],
    features: [
      { icon: <BedDouble size={18} />, text: "Çift Kişilik Yatak" },
      { icon: <Wifi size={18} />, text: "Ücretsiz Wi-Fi" },
      { icon: <Tv size={18} />, text: "Smart TV" },
      { icon: <Wind size={18} />, text: "Klima" }
    ]
  },
  {
    id: 2,
    title: "Geniş 2+1 Aile Dairesi",
    description: "Aileler ve kalabalık gruplar için ferah, tam donanımlı lüks daire.",
    images: [
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/vkyasyy0_2%2B1%20kapak.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/c89vdxyn_D-101.jpeg",
      "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/0wlrdbhw_D-102.jpeg"
    ],
    features: [
      { icon: <BedDouble size={18} />, text: "2 Yatak Odası" },
      { icon: <Coffee size={18} />, text: "Tam Donanımlı Mutfak" },
      { icon: <Wifi size={18} />, text: "Yüksek Hızlı İnternet" },
      { icon: <Bath size={18} />, text: "Çamaşır Makinesi" }
    ]
  }
];

const ApartmentCard = ({ apt, index }) => {
  const [currentIdx, setCurrentIdx] = useState(0);

  const nextImage = () => {
    setCurrentIdx((prev) => (prev + 1) % apt.images.length);
  };

  const prevImage = () => {
    setCurrentIdx((prev) => (prev - 1 + apt.images.length) % apt.images.length);
  };

  return (
    <Reveal delay={index * 0.15} className="h-full">
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden group hover:border-yellow-500/50 transition-colors duration-300 hover:shadow-[0_0_40px_-10px_rgba(234,179,8,0.25)] h-full flex flex-col"
        data-testid={`apartment-card-${apt.id}`}
      >
        <div className="relative h-64 md:h-72 overflow-hidden shrink-0">
          <a
            href="#gallery"
            data-testid={`apartment-gallery-link-${apt.id}`}
            aria-label={`${apt.title} — tüm fotoğrafları galeride gör`}
            className="absolute inset-0 block z-0"
          >
            <img
              src={apt.images[currentIdx]}
              alt={`${apt.title} - Görsel ${currentIdx + 1}`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </a>
          <div className="absolute inset-0 bg-black/20 z-10 pointer-events-none"></div>
          <div className="absolute top-3 right-3 z-10 pointer-events-none opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
            <span className="bg-black/60 backdrop-blur-sm text-yellow-400 text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full border border-yellow-500/30">
              Galeride Gör
            </span>
          </div>

          {apt.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                data-testid={`apartment-prev-${apt.id}`}
                aria-label="Önceki fotoğraf"
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-yellow-500 text-white hover:text-black p-2 rounded-full backdrop-blur-sm transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextImage}
                data-testid={`apartment-next-${apt.id}`}
                aria-label="Sonraki fotoğraf"
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-yellow-500 text-white hover:text-black p-2 rounded-full backdrop-blur-sm transition-colors"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-20">
                {apt.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIdx(idx)}
                    data-testid={`apartment-dot-${apt.id}-${idx}`}
                    aria-label={`Fotoğraf ${idx + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors ${idx === currentIdx ? 'bg-yellow-500 scale-125' : 'bg-white/50 hover:bg-white'}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-8 flex flex-col flex-1">
          <h4 className="font-display text-2xl font-semibold text-white mb-3">{apt.title}</h4>
          <p className="text-zinc-400 mb-6 text-sm leading-relaxed">{apt.description}</p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            {apt.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-zinc-300 text-sm">
                <span className="text-yellow-500">{feature.icon}</span>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <a
            href="tel:+905416410171"
            data-testid={`apartment-call-${apt.id}`}
            className="mt-auto block w-full text-center bg-zinc-800 hover:bg-yellow-500 hover:text-black text-white py-3 rounded-xl font-semibold transition-colors duration-300 border border-zinc-700 hover:border-yellow-500"
          >
            Fiyat Bilgisi Al
          </a>
        </div>
      </motion.div>
    </Reveal>
  );
};

const Apartments = () => {
  return (
    <section id="apartments" className="py-24 bg-zinc-950" data-testid="apartments-section">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {apartments.map((apt, idx) => (
            <ApartmentCard key={apt.id} apt={apt} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Apartments;
